import * as React from 'react';
import { formatPhoneNumber } from '../lib/formatPhoneNumber';
import { haversineDistance } from '../lib/haversineDistance';
import { LocationPost } from '../types';
import { TopBar } from './TopBar';
import { handleAppointmentClick } from '../lib/handleAppointmentClick';
import sanitizeHtml from 'sanitize-html';

interface Props {
	location: LocationPost;
	currentLatitude: number;
	currentLongitude: number;
	onBackClick: () => void;
}

export const LocationDetails = ({
	location,
	currentLatitude,
	currentLongitude,
	onBackClick,
}: Props) => {
	const [longitude, latitude] = location.coordinates;
	const distance = haversineDistance(currentLatitude, currentLongitude, +latitude, +longitude);
	const permalink = `${window.findaConnector.locationBaseUrl}/${location.slug}`;
	const testCategories = location.meta.test_categories.length
		? location.meta.test_categories.split(';')
		: '';

	return (
		<div className="fi-location-details">
			<TopBar onClick={onBackClick}>Back to Search</TopBar>
			<div className="fi-location-details__info">
				<div className="fi-location-details__details">
					<h3
						className="fi-location-item__title"
						dangerouslySetInnerHTML={{ __html: sanitizeHtml(location.title) }}
					/>
					<div className="fi-location-item__address">
						{location.meta.address}
						<br />
						{location.meta.city}, {location.meta.state} {location.meta.postal}
					</div>
					{location.meta.practice_phone_number && (
						<div className="fi-location-item__phone">
							<a href={`tel:${location.meta.practice_phone_number}`}>
								{formatPhoneNumber(location.meta.practice_phone_number)}
							</a>
						</div>
					)}
					{!!location.meta.well_proz_shop_url.length && (
						<div className="fi-location-item__wellproz">
							<svg
								width="25"
								height="38"
								viewBox="0 0 25 38"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g id="wellproz logo 1" clipPath="url(#clip0_8217_11053)">
									<path
										id="Vector"
										fillRule="evenodd"
										clipRule="evenodd"
										d="M24.0069 24.5703V36.482H11.9861C12.3442 31.6174 15.4427 27.3705 19.9907 25.5104C21.2701 24.9861 22.6261 24.6687 24.0069 24.5703ZM11.5544 30.8483L11.414 31.1403C11.321 31.3385 11.2325 31.5396 11.1484 31.7435C10.5197 33.2517 10.1451 34.8526 10.0399 36.482H0.287382C0.322557 35.5776 0.407232 34.6759 0.54103 33.7807C3.62182 33.6413 6.65471 32.97 9.50399 31.7969C10.2038 31.5076 10.8881 31.1914 11.5568 30.8481L11.5544 30.8483ZM23.2556 12.9779C22.9528 14.2638 22.5443 15.523 22.0341 16.7429C20.7887 19.7137 18.9657 22.4126 16.6687 24.6859C14.3746 26.962 11.651 28.7686 8.65308 30.0027C6.16089 31.0247 3.51576 31.6327 0.824022 31.8024C1.1268 30.5164 1.53532 29.2572 2.04548 28.0373C4.56407 22.04 9.37432 17.2732 15.4265 14.7775C17.9187 13.7555 20.5639 13.1475 23.2556 12.9779ZM7.05825 1.94149C9.93324 -0.651071 14.3267 -0.651071 17.2017 1.94149C19.0766 3.63215 19.9045 6.06346 19.6062 8.40463H23.8996C23.8644 9.30943 23.7797 10.2116 23.646 11.1072C21.7927 11.1911 19.9569 11.4676 18.1678 11.93L18.1554 11.9483L18.1002 11.9476C16.9385 12.2511 15.7967 12.6333 14.683 13.092C13.9831 13.3814 13.2989 13.6979 12.6301 14.0414C12.7758 13.7487 12.9119 13.45 13.0385 13.1455C13.667 11.6364 14.0415 10.0347 14.147 8.40463L17.4647 8.40476C17.6097 7.59637 17.569 6.74823 17.3193 5.92354C16.6318 3.65275 14.5225 2.09769 12.13 2.09769C9.7374 2.09769 7.62815 3.65275 6.94059 5.92354C6.69088 6.74823 6.65017 7.59637 6.79525 8.40476L12.2008 8.40463C12.1013 9.77249 11.7812 11.1156 11.2526 12.3829C10.5952 13.9498 9.63269 15.3729 8.42001 16.5707C7.21116 17.772 5.77524 18.7254 4.19435 19.3765C2.91555 19.9004 1.56024 20.2176 0.180054 20.3162V8.40463H4.65374C4.35547 6.06346 5.18335 3.63215 7.05825 1.94149Z"
										fill="#2ED3C5"
									/>
								</g>
								<defs>
									<clipPath id="clip0_8217_11053">
										<rect
											width="24"
											height="37.0286"
											fill="white"
											transform="translate(0.180054)"
										/>
									</clipPath>
								</defs>
							</svg>
							<a
								href={location.meta.well_proz_shop_url}
								target="_blank"
								rel="noreferrer"
							>
								Purchase at Well ProZ Shop
							</a>
						</div>
					)}
					{location.meta.service_locations.includes('Virtual') && (
						<div className="fi-location-item__virtual-mobile">
							Offers Virtual Appointments
						</div>
					)}
					{testCategories && (
						<div className="fi-test-categories">
							<h4 className="fi-test-categories__title">
								Test Categories Available:
							</h4>
							<p
								className="fi-test-categories__text"
								dangerouslySetInnerHTML={{
									__html: sanitizeHtml(testCategories.join(', ')),
								}}
							/>
						</div>
					)}
					<div className="fi-btn-group">
						<a
							className="btn-primary"
							href={permalink + '#request-appointment'}
							onClick={(e) => handleAppointmentClick(e, location.title)}
						>
							Request an Appointment
						</a>

						<a className="btn-tertiary" href={permalink}>
							Practice Details
						</a>
					</div>
				</div>
				<div className="fi-location-item__right">
					<div className="fi-location-item__distance">
						<div className="fi-location-item__distance-svg"></div>
						{isNaN(distance) ? '-.--' : distance.toFixed(2)}
						mi
					</div>
					{location.meta.service_locations.includes('Virtual') && (
						<div className="virtual-hover">
							<p className="virtual-hover__text">Offers Virtual Appointments</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
